import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * User Context for Authentication and Tier Management
 * Provides user data, subscription tier, and AI usage limits across the app
 */

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize user data
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      // In a real app, this would be an API call
      // For now, we'll simulate with localStorage and defaults
      
      const storedUser = localStorage.getItem('plugly_user');
      const storedSubscription = localStorage.getItem('plugly_subscription');
      const storedTier = localStorage.getItem('userTier'); // Check for tier from signup
      
      // Detect tier from URL path if not stored
      const detectTierFromPath = () => {
        const path = window.location.pathname;
        console.log('🔍 UserContext detectTierFromPath:', { path, storedTier });
        if (path.startsWith('/free/')) {
          console.log('✅ Detected FREE tier from path');
          return 'free';
        }
        if (path.startsWith('/smb/')) {
          console.log('✅ Detected SMB tier from path');
          return 'smb';
        }
        if (path.startsWith('/professional/')) {
          console.log('✅ Detected PROFESSIONAL tier from path');
          return 'professional';
        }
        if (path.startsWith('/medium/')) {
          console.log('✅ Detected MEDIUM tier from path (mapping to professional)');
          return 'professional'; // Legacy support
        }
        if (path.startsWith('/enterprise/')) {
          console.log('✅ Detected ENTERPRISE tier from path');
          return 'enterprise';
        }
        const defaultTier = storedTier || 'free';
        console.log('⚠️ No tier detected from path, using default:', defaultTier);
        return defaultTier; // Default to free tier
      };
      
      if (storedUser && storedSubscription) {
        const user = JSON.parse(storedUser);
        let subscription = JSON.parse(storedSubscription);
        
        // If there's a stored tier from signup, use that
        if (storedTier && ['free', 'smb', 'professional', 'enterprise'].includes(storedTier)) {
          subscription.tier = storedTier;
          console.log('🎯 UserContext: Using stored tier from signup:', storedTier);
        } else if (storedTier === 'medium') {
          subscription.tier = 'professional'; // Map legacy medium to professional
          console.log('🎯 UserContext: Mapped legacy medium tier to professional');
        }
        
        setUser(user);
        setSubscription(subscription);
        console.log('✅ UserContext: Loaded authenticated user:', user.email);
      } else {
        // No stored user - user is not authenticated
        console.log('⚠️ UserContext: No authenticated user found. User must sign up or log in.');
        setUser(null);
        setSubscription(null);
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSubscription = (newSubscription) => {
    setSubscription(newSubscription);
    localStorage.setItem('plugly_subscription', JSON.stringify(newSubscription));
  };

  const trackAIUsage = () => {
    if (subscription?.aiLimits) {
      const updatedSubscription = {
        ...subscription,
        aiLimits: {
          ...subscription.aiLimits,
          queriesUsed: subscription.aiLimits.queriesUsed + 1
        }
      };
      updateSubscription(updatedSubscription);
      return updatedSubscription.aiLimits;
    }
    return null;
  };

  const checkAILimit = () => {
    if (!subscription?.aiLimits) return { canUse: true, remaining: 'unlimited' };
    
    const { monthlyQueries, queriesUsed } = subscription.aiLimits;
    
    if (monthlyQueries === 'unlimited') {
      return { canUse: true, remaining: 'unlimited' };
    }
    
    const remaining = monthlyQueries - queriesUsed;
    return {
      canUse: remaining > 0,
      remaining: remaining,
      percentUsed: (queriesUsed / monthlyQueries) * 100
    };
  };

  const upgradeTier = (newTier) => {
    const tierConfigs = {
      smb: {
        tier: 'smb',
        plan: 'Small Business',
        aiLimits: {
          monthlyQueries: 500,
          queriesUsed: 0,
          lastReset: new Date().toISOString(),
          maxSuggestions: 3,
          aiModel: 'GPT-3.5 Turbo',
          features: ['basic-optimization', 'simple-suggestions']
        },
        features: {
          crossPageWorkflows: false,
          voiceInterface: false,
          fileUpload: false,
          teamSharing: false,
          customIntegrations: false
        }
      },
      professional: {
        tier: 'professional',
        plan: 'Professional',
        aiLimits: {
          monthlyQueries: 2000,
          queriesUsed: 0,
          lastReset: new Date().toISOString(),
          maxSuggestions: 5,
          aiModel: 'GPT-4',
          features: [
            'advanced-optimization',
            'brand-voice',
            'automation',
            'custom-reports',
            'team-management',
            'customer-analysis'
          ]
        },
        features: {
          crossPageWorkflows: true,
          voiceInterface: false,
          fileUpload: true,
          teamSharing: false,
          customIntegrations: false
        }
      },
      enterprise: {
        tier: 'enterprise',
        plan: 'Enterprise',
        aiLimits: {
          monthlyQueries: 'unlimited',
          queriesUsed: 0,
          lastReset: new Date().toISOString(),
          maxSuggestions: 10,
          aiModel: 'GPT-4 Turbo',
          features: [
            'strategic-planning',
            'market-research',
            'predictive-analytics',
            'custom-personas',
            'brand-management',
            'ad-operations',
            'security-governance',
            'ai-orchestration'
          ]
        },
        features: {
          crossPageWorkflows: true,
          voiceInterface: true,
          fileUpload: true,
          teamSharing: true,
          customIntegrations: true
        }
      }
    };

    const newConfig = tierConfigs[newTier];
    if (newConfig) {
      const updatedSubscription = {
        ...subscription,
        ...newConfig,
        status: 'active',
        startDate: new Date().toISOString()
      };
      updateSubscription(updatedSubscription);
    }
  };

  const logout = () => {
    setUser(null);
    setSubscription(null);
    // Clear all authentication and user-related data
    localStorage.removeItem('plugly_user');
    localStorage.removeItem('plugly_subscription');
    localStorage.removeItem('plugly_ai_session');
    localStorage.removeItem('userTier');
    localStorage.removeItem('userActualTier');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    localStorage.removeItem('firebaseUser');
    console.log('✅ User logged out - all data cleared');
  };

  const value = {
    user,
    subscription,
    loading,
    isAuthenticated: !!user, // User is authenticated if user object exists
    trackAIUsage,
    checkAILimit,
    upgradeTier,
    updateSubscription,
    logout,
    
    // Convenience getters
    userTier: subscription?.tier || 'free',
    aiLimits: subscription?.aiLimits || null,
    features: subscription?.features || {},
    
    // Helper functions
    hasFeature: (feature) => subscription?.features?.[feature] || false,
    canUseAI: () => checkAILimit().canUse,
    getAIModel: () => subscription?.aiLimits?.aiModel || 'GPT-3.5 Turbo'
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
